/* GameEditorRenderer.js */

'use strict';
const BreadCrumbComponent = require('./utils/breadcrumb/BreadcrumbComponent');
const GameEditorTitleComponent = require('./utils/gameeditortitlecomponent/GameEditorTitleComponent');
const GameEditorViewActionsComponent = require('./utils/gameeditorviewactionscomponent/GameEditorViewActionsComponent');
const GameEditorCabinetComponent = require('./utils/gameeditorcabinetcomponent/GameEditorCabinetComponent');
const _ = require('lodash');

class GameEditorRenderer {
  constructor(model) {
    this._model = model;
    this._rootElem = document.createElement('game-editor-gl');
    this._rootElem.component = this._model;
    this._logger = this.model.logger;
    this._headerElement = undefined;

    /*
            NOTE: The GameEditorComponent acts as a facade to these sub-components below, and acts directly on them.
            Generally, this is a *really bad* idea. In this case, the component pattern is still being used because
            the editor-layout will expect an api matching the component pattern.

            In other words.... don't try this at home.
        */

    this._breadCrumbCmpnt = new BreadCrumbComponent(this._logger);
    this._titleCmpnt = new GameEditorTitleComponent(this._logger);
    this._cabinetCmpnt = new GameEditorCabinetComponent(this._logger);
    this._viewContainerElem = document.createElement('game-editor-view-container-gl');
    this._viewActionsCmpnt = new GameEditorViewActionsComponent(this._logger);

    this._model.__setBreadCrumbComponent(this._breadCrumbCmpnt);
    this._model.__setTitleComponent(this._titleCmpnt);
    this._model.__setViewActionsComponent(this._viewActionsCmpnt);
    this._model.__setCabinetComponent(this._cabinetCmpnt);
    this._model.__setViewContainerElement(this._viewContainerElem);

    this._registerListeners();
  }
  /********************************************/
  /* START PRIVATE COMPONENT RENDERER METHODS */
  /********************************************/
  _registerListeners() {
    this._cabinetCmpnt.on(
      'UserActionsPinned',
      (this._onUserActionsPinned = this._onUserActionsPinned.bind(this))
    );
    this._cabinetCmpnt.on(
      'UserViewsPinned',
      (this._onUserViewsPinned = this._onUserViewsPinned.bind(this))
    );
    this._model.on('RetardActions', (this._onRetardedActions = this._onRetardedActions.bind(this)));
  }
  _unregisterListeners() {
    this._cabinetCmpnt.removeListener('UserActionsPinned', this._onUserActionsPinned);
    this._cabinetCmpnt.removeListener('UserViewsPinned', this._onUserViewsPinned);
    this._model.removeListener('RetardActions', this._onRetardedActions);
  }
  /*******************************************/
  /* END PRIVATE COMPONENT RENDERER METHODS  */
  /* START PUBLIC COMPONENT RENDERER METHODS */
  /*******************************************/
  shutdown() {
    this._unregisterListeners();
    let promArr = [
      this._breadCrumbCmpnt.shutdown(),
      this._titleCmpnt.shutdown(),
      this._cabinetCmpnt.shutdown(),
      this._viewActionsCmpnt.shutdown(),
    ];

    return Promise.all(promArr).then(
      () => {
        this._headerElement = undefined;
        this._breadCrumbCmpnt = undefined;
        this._titleCmpnt = undefined;
        this._cabinetCmpnt = undefined;
        this._viewActionsCmpnt = undefined;
        this._viewContainerElem.remove();
        this._viewContainerElem = undefined;
        this._model = undefined;
        this._rootElem.innerHTML = ``;
        this._rootElem.remove();
        this._rootElem = undefined;
        this._logger = undefined;
        return Promise.resolve();
      },
      (err) => {
        this._logger.warn(err, 'GameEditorComponent.shutdown() error');
      }
    );
  }
  /**
   * Stateless method to completely rebuild this component from the _rootElem down.
   * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
   * Promise is returned the caller will assume the Renderer has completed rendering.
   */
  render() {
    if (!this._rootElem.hasChildNodes()) {
      //TODO - Start HeaderComponent - At some point this header will become its own component, but for now it is a sum of parts...
      this._rootElem.innerHTML = `
                <game-editor-header-gl class='ge-header'>
                
                    <game-editor-header-left-content class='ge-header-left'>
                    </game-editor-header-left-content>
                    
                    <game-editor-header-middle-content class='ge-header-middle'>
                    </game-editor-header-middle-content>
                    
                    <game-editor-header-right-content class='ge-header-right'>                                
                    </game-editor-header-right-content>
                    
                </game-editor-header-gl>
            `;
      this._headerElement = this._rootElem.querySelector('.ge-header');
      let headerLeft = this._rootElem.querySelector('.ge-header-left');
      let headerMiddle = this._rootElem.querySelector('.ge-header-middle');
      let headerRight = this._rootElem.querySelector('.ge-header-right');
      headerLeft.appendChild(this._breadCrumbCmpnt.renderer.rootElement);
      headerMiddle.appendChild(this._titleCmpnt.renderer.rootElement);
      headerRight.appendChild(this._cabinetCmpnt.renderer.rootElement);
      //TODO - End HeaderComponent

      this._rootElem.insertBefore(
        this._model._modalUserFeedback.renderer.rootElement,
        this._headerElement
      );
      this._rootElem.appendChild(this._viewContainerElem);
      this._rootElem.appendChild(this._viewActionsCmpnt.renderer.rootElement);
    }

    return this._model.resetModel(true).then(() => {
      let promArr = [
        this._titleCmpnt.renderer.render(),
        this._breadCrumbCmpnt.renderer.render(),
        this._cabinetCmpnt.renderer.render(),
        this._viewActionsCmpnt.renderer.render(),
      ];
      return Promise.all(promArr).then(() => {
        if (!_.isEmpty(this.model.editorState)) {
          let state = this.model.editorState;
          let fqn = state.fqn;
          let breadcrumb = state.breadcrumb;
          let viewState = state.viewState;
          return this.model.setActiveView(fqn, breadcrumb, viewState);
        }
      });
    });
  }
  get rootElement() {
    return this.getRootElement();
  }
  getRootElement() {
    return this._rootElem;
  }
  get model() {
    return this.getModel();
  }
  getModel() {
    return this._model;
  }
  /*******************************************/
  /* END COMPONENT RENDERER METHODS */
  /* START PRIVATE RENDER METHODS */
  /*******************************************/

  /*******************************************/
  /* END PRIVATE RENDER METHODS */
  /* START HANDLER METHODS */
  /*******************************************/

  _onDescriptionIconClicked() {}

  _onUserViewsPinned(eventData) {
    this._model._triggerUserViewsPinned(eventData);
  }

  _onUserActionsPinned(eventData) {
    this._model._triggerUserActionsPinned(eventData);
  }

  _onRetardedActions(retarded) {
    this._cabinetCmpnt.retardActions(retarded);
  }

  /*******************************************/
  /* END HANDLER METHODS */
  /* START PRIVATE API METHODS */
  /*******************************************/

  /*******************************************/
  /*  END PRIVATE API METHODS */
  /*******************************************/
}
module.exports = GameEditorRenderer;
